import { useMemo } from 'react';
import {
  Calendar as AriaCalendar,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarProps as AriaCalendarProps,
  CalendarCell,
  CalendarGrid,
  CalendarGridBody,
  CalendarHeaderCell,
  DateValue,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { ChevronLeftIcon, ChevronRightIcon } from '@page/icons';

import { composeTailwindRenderProps, focusRing } from '../../lib/styles';
import { IconButton } from '../IconButton';
import { FieldError } from '../Field';
import { useCalendarContext } from './Calendar.utils';
import { MonthDropdown } from './MonthDropdown';
import { YearDropdown } from './YearDropdown';
import { attempt } from '@page/core/utils/function';

const cellStyles = tv({
  extend: focusRing,
  base: 'w-[calc(100cqw/7)] aspect-square font-body-sm cursor-default rounded-full flex items-center justify-center [-webkit-tap-highlight-color:transparent]',
  variants: {
    isSelected: {
      false:
        'text-text-primary hover:bg-bg-inter-secondary-hover active:bg-bg-inter-secondary-pressed pressed:bg-bg-inter-secondary-pressed',
      true: 'bg-blue-2 invalid:bg-red-5 font-semibold',
    },
    isDisabled: {
      true: 'text-text-disabled',
    },
  },
});

export interface CalendarProps<T extends DateValue> extends Omit<AriaCalendarProps<T>, 'visibleDuration'> {
  error?: string;
}

export function Calendar<T extends DateValue>({ error, ...props }: CalendarProps<T>) {
  return (
    <AriaCalendar
      {...props}
      className={composeTailwindRenderProps(
        props.className,
        'flex flex-col font-sans w-[calc(9*var(--spacing)*7)] max-w-full @container',
      )}
    >
      <CalendarHeader />

      <CalendarGrid className="border-spacing-0">
        <CalendarGridHeader />
        <CalendarGridBody>{(date) => <CalendarCell date={date} className={cellStyles} />}</CalendarGridBody>
      </CalendarGrid>

      {error && <FieldError slot="errorMessage">{error}</FieldError>}
    </AriaCalendar>
  );
}

export function CalendarHeader() {
  const state = useCalendarContext();

  const canMovePrevious = useMemo(() => {
    const [error, isInvalid] = attempt(() => state.isPreviousVisibleRangeInvalid());

    return !!error || !isInvalid;
  }, [state]);

  const canMoveNext = useMemo(() => {
    const [error, isInvalid] = attempt(() => state.isNextVisibleRangeInvalid());

    return !!error || !isInvalid;
  }, [state]);

  return (
    <header className="flex! items-center gap-1 pb-4 px-1">
      <IconButton
        variant="tertiary"
        size="md"
        onClick={state.focusPreviousPage}
        disabled={!canMovePrevious}
        aria-label="Previous month"
      >
        <ChevronLeftIcon />
      </IconButton>

      <MonthDropdown className="flex-1" />
      <YearDropdown className="flex-1" />

      <IconButton
        variant="tertiary"
        size="md"
        onClick={state.focusNextPage}
        disabled={!canMoveNext}
        aria-label="Next month"
      >
        <ChevronRightIcon />
      </IconButton>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="font-body-sm text-text-tertiary font-semibold">{day}</CalendarHeaderCell>
      )}
    </AriaCalendarGridHeader>
  );
}
